import type {
	BaseInteraction,
	FestivalInteraction,
	FilterStrategy,
	SetShowInteraction,
	SortStrategy,
} from "./types";

/**
 * Sort Strategies
 */

// Generic priority sort - can be customized with priority function
export const createPrioritySort = <T extends BaseInteraction>(
	getPriority: (item: T) => number,
): SortStrategy<T> => {
	return (items: T[]) => {
		return [...items].sort((a, b) => getPriority(b) - getPriority(a));
	};
};

// Set/Show engagement priority sort
export const setShowEngagementSort: SortStrategy<SetShowInteraction> =
	createPrioritySort((item) => {
		if (item.saw_live) return 6;
		if (item.saw_stream) return 5;
		if (item.must_see) return 4;
		if (item.loved) return 3;
		if (item.rating && item.rating > 0) return 2;
		if (item.want_to_see) return 1;
		return 0;
	});

// Festival attendance priority sort
export const festivalAttendanceSort: SortStrategy<FestivalInteraction> =
	createPrioritySort((item) => {
		if (item.festival_status === "going") return 3;
		if (item.festival_status === "went") return 2;
		if (item.festival_status === "want_to_go") return 1;
		return 0;
	});

// Alphabetical sort by display name
export const alphabeticalSort: SortStrategy<BaseInteraction> = (items) => {
	return [...items].sort((a, b) =>
		a.display_name.localeCompare(b.display_name),
	);
};

/**
 * Filter Strategies
 */

// Filter to only show users with any engagement (including attendance status)
export const hasEngagementFilter: FilterStrategy<SetShowInteraction> = (
	items,
) => {
	return items.filter(
		(item) =>
			item.show_status ||  // Include users who marked attendance
			item.rating ||
			item.loved ||
			item.must_see ||
			item.want_to_see ||
			item.saw_live ||
			item.saw_stream,
	);
};

// Filter to only show users with festival status
export const hasFestivalStatusFilter: FilterStrategy<FestivalInteraction> = (
	items,
) => {
	return items.filter((item) => item.festival_status);
};

// Filter to only show users with reviews (rating or comment)
export const hasReviewFilter: FilterStrategy<SetShowInteraction> = (items) => {
	return items.filter(
		(item) => (item.rating && item.rating > 0) || item.content,
	);
};

// Filter to only show users who attended (saw live or stream)
export const hasAttendedFilter: FilterStrategy<SetShowInteraction> = (
	items,
) => {
	return items.filter((item) => item.saw_live || item.saw_stream);
};

// Create custom filter based on field presence
export const createFieldFilter = <T extends BaseInteraction>(
	field: keyof T,
): FilterStrategy<T> => {
	return (items: T[]) => items.filter((item) => Boolean(item[field]));
};
